import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Group, Loader, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconCheck,
  IconDeviceFloppy,
  IconEye,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSend,
  IconSparkles,
} from '@tabler/icons-react';
import { EssayNode } from '../../hooks/useEssayApi';
import {
  QuarterlyReport,
  isGenerationStale,
  saveQuarterlyItems,
} from '../../hooks/useQuarterlyApi';
import { ApiError } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { EssaySortableList } from './EssaySortableList';
import { NodeListPreview, pruneNodes } from './NodeListPreview';
import { QuarterlySources } from './QuarterlySources';
import { ReturnForReworkButton, ReturnedNotice } from './ReturnForRework';
import { addChild, insertAfter, moveNode, newNode, removeNode, setText } from './essayTree';
import { useDocumentAutosave, type SaveState } from './useDocumentAutosave';

interface Props {
  report: QuarterlyReport;
  readOnly?: boolean;
  canSubmit?: boolean;
  isStatusPending?: boolean;
  isGenerating?: boolean;
  onGenerate?: () => void;
  onSubmit?: () => void;
  onUnsubmit?: () => void;
  /** Может ли смотрящий вернуть отчёт автору. */
  canReturn?: boolean;
  onReturn?: (comment: string) => Promise<unknown>;
  isReturnPending?: boolean;
  /** По отчёту есть оценки — текст заморожен. */
  isScored?: boolean;
}

function SaveBadge({ state, t }: { state: SaveState; t: (k: string) => string }) {
  if (state === 'saving') return <Badge variant="light">{t('essays.saving')}</Badge>;
  if (state === 'saved')
    return (
      <Badge variant="light" color="teal" leftSection={<IconCheck size={12} />}>
        {t('essays.saved')}
      </Badge>
    );
  if (state === 'error')
    return (
      <Badge variant="light" color="red" leftSection={<IconAlertTriangle size={12} />}>
        {t('essays.saveError')}
      </Badge>
    );
  return null;
}

/**
 * Квартальный отчёт: одно дерево «направления → задачи» вместо четырёх разделов
 * месячного эссе.
 *
 * Пока идёт сборка, редактор заблокирован — иначе человек, не дождавшись,
 * начнёт писать сам, а пришедший ответ нейронки перепишет набранное. Сервер
 * отказывает в правке ровно по тому же признаку, так что блокировка здесь —
 * не единственная защита, а объяснение происходящего.
 */
export const QuarterlyEditor: React.FC<Props> = ({
  report,
  readOnly,
  canSubmit,
  isStatusPending,
  isGenerating,
  onGenerate,
  onSubmit,
  onUnsubmit,
  canReturn,
  onReturn,
  isReturnPending,
  isScored,
}) => {
  const { t } = useTranslation();
  const [focusId, setFocusId] = React.useState<string | null>(null);

  const isRunning = report.generation.state === 'running';
  const isStale = isRunning && isGenerationStale(report.generation.startedAt);
  // Зависшая сборка — то же для человека, что и упавшая: показываем причину и
  // даём кнопку. Сервер такую генерацию уже считает мёртвой и перехватит её.
  const hasFailed = report.generation.state === 'failed' || isStale;
  const isLocked = readOnly || (isRunning && !isStale);

  const errorMessage = React.useCallback(
    (error: unknown) =>
      error instanceof ApiError && error.status === 403
        ? t('quarterly.submittedNotice')
        : t('essays.saveFailedMessage'),
    [t],
  );

  // Сброс не только по смене отчёта, но и по новой сборке: пока идёт опрос, id
  // не меняется, и без generatedAt в ключе собранное нейронкой дерево не
  // доехало бы до редактора — человек смотрел бы на пустой экран до перезагрузки.
  const resetKey = `${report._id}:${report.generatedAt ?? ''}`;
  const initial = React.useMemo(() => report.items || [], [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ключом сброса идёт resetKey, а не id: сохраняем всё равно по id отчёта,
  // поэтому функция сохранения берёт его сама и параметр ключа игнорирует.
  const save = React.useCallback(
    (_key: string, items: EssayNode[]) => saveQuarterlyItems(report._id, items),
    [report._id],
  );

  const { content, update, saveNow, saveState, saveErrorMessage } = useDocumentAutosave<
    EssayNode[]
  >({
    docId: resetKey,
    initial,
    readOnly: isLocked,
    save,
    errorMessage,
    onDetachedFailure: (error) =>
      showToast({ title: t('essays.saveError'), message: errorMessage(error), color: 'red' }),
  });

  // Сначала дожимаем автосейв: отложенный PUT ушёл бы уже после смены статуса и
  // получил бы 403.
  const handleSubmit = async () => {
    if (await saveNow()) onSubmit?.();
  };

  const handleAddTop = () => {
    const node = newNode();
    update((items) => [...items, node]);
    setFocusId(node.id);
  };

  const handleAddSibling = (id: string) => {
    const node = newNode();
    update((items) => insertAfter(items, id, node));
    setFocusId(node.id);
  };

  const isEmpty = pruneNodes(content).length === 0;

  // Предпросмотр: тот же вид, в котором отчёт увидят оценивающие. Человек пишет
  // в полях ввода, а читают его нумерованным списком — до сдачи это разные
  // картинки, и посмотреть на вторую стоит заранее.
  const [isPreview, setIsPreview] = React.useState(false);
  const isReading = readOnly || isPreview;

  return (
    <Stack gap="md">
      {isRunning && !isStale && (
        <Alert color="blue" variant="light" icon={<Loader size={16} />}>
          {t('quarterly.generating')}
        </Alert>
      )}

      {hasFailed && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text size="sm">{t('quarterly.generateFailed')}</Text>
            {onGenerate && !readOnly && (
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconRefresh size={14} />}
                onClick={onGenerate}
                loading={isGenerating}
              >
                {t('quarterly.retry')}
              </Button>
            )}
          </Group>
        </Alert>
      )}

      <ReturnedNotice note={report.returnedForRework} />

      {isScored ? (
        <Alert color="teal" variant="light" icon={<IconCheck size={16} />}>
          {t('quarterly.scoredNotice')}
        </Alert>
      ) : (
        // «Верните в черновик» — совет автору; лиду в панели отчёта он не по делу.
        report.status === 'submitted' &&
        readOnly &&
        canSubmit && (
          <Alert color="teal" variant="light" icon={<IconCheck size={16} />}>
            {t('quarterly.submittedNotice')}
          </Alert>
        )
      )}

      {saveErrorMessage && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          {saveErrorMessage}
        </Alert>
      )}

      <Group justify="space-between" wrap="wrap" gap="sm">
        <QuarterlySources
          sources={report.sources}
          generatedAt={report.generatedAt}
          canEdit={!isLocked}
        />
        <Group gap="sm">
          {!isLocked && <SaveBadge state={saveState} t={t} />}
          {!isLocked && !isEmpty && (
            <Button
              variant="default"
              leftSection={isPreview ? <IconPencil size={16} /> : <IconEye size={16} />}
              onClick={() => setIsPreview((v) => !v)}
            >
              {t(isPreview ? 'quarterly.backToEditing' : 'quarterly.preview')}
            </Button>
          )}
          {!isLocked && onGenerate && (
            <Button
              variant="light"
              leftSection={<IconSparkles size={16} />}
              onClick={onGenerate}
              loading={isGenerating || isRunning}
            >
              {isEmpty ? t('quarterly.generate') : t('quarterly.regenerate')}
            </Button>
          )}
          {!isLocked && (
            <Button
              variant="default"
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={() => void saveNow()}
              loading={saveState === 'saving'}
            >
              {t('common.save')}
            </Button>
          )}
          {canReturn && onReturn && report.status === 'submitted' && (
            <ReturnForReworkButton onReturn={onReturn} isPending={isReturnPending} />
          )}
          {canSubmit &&
            !isScored &&
            (report.status === 'submitted' ? (
              <Button
                variant="light"
                leftSection={<IconArrowBackUp size={16} />}
                onClick={() => onUnsubmit?.()}
                loading={isStatusPending}
              >
                {t('essays.unsubmit')}
              </Button>
            ) : (
              <Button
                leftSection={<IconSend size={16} />}
                onClick={() => void handleSubmit()}
                loading={isStatusPending || saveState === 'saving'}
                disabled={isLocked}
              >
                {t('essays.submit')}
              </Button>
            ))}
        </Group>
      </Group>

      <Card withBorder radius="md" padding="md">
        {isReading ? (
          isEmpty ? (
            <Text c="dimmed" size="sm">
              {t('quarterly.empty')}
            </Text>
          ) : (
            <NodeListPreview nodes={pruneNodes(content)} />
          )
        ) : (
          <>
            {content.length === 0 && (
              <Text c="dimmed" size="sm" mb="sm">
                {t('quarterly.empty')}
              </Text>
            )}
            <EssaySortableList
              nodes={content}
              readOnly={isLocked}
              focusId={focusId}
              onChangeText={(id, text) => update((items) => setText(items, id, text))}
              onAddChild={(id) => update((items) => addChild(items, id))}
              onRemove={(id) => update((items) => removeNode(items, id))}
              onAddSibling={handleAddSibling}
              onMove={(activeId, parentId, index) =>
                update((items) => moveNode(items, activeId, parentId, index))
              }
            />
            {!isLocked && (
              <Button
                variant="subtle"
                size="xs"
                mt="sm"
                leftSection={<IconPlus size={14} />}
                onClick={handleAddTop}
              >
                {t('quarterly.addGroup')}
              </Button>
            )}
          </>
        )}
      </Card>
    </Stack>
  );
};
